<template>
  <div>
    <ts-loading-banner :loading="loading">
      <div class="ts-space-y-4">
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.paymentDate')
          }}</label>
          <div class="ts-w-64">
            <a-date-picker
              v-model="model.payment_date"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              class="ts-w-full"
            ></a-date-picker>
            <div class="validate ts-text-xs" v-if="errors.has('payment_date')">
              {{ errors.first('payment_date') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.stockInNumber')
          }}</label>
          <div class="ts-w-64">
            <a-select
              v-model="model.stock_in_id"
              :options="stockIn"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
              @change="onStockChange"
              class="ts-w-full"
            ></a-select>
            <div class="validate ts-text-xs" v-if="errors.has('stock_in_id')">
              {{ errors.first('stock_in_id') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.referenceNumber')
          }}</label>
          <div class="ts-w-64">
            <input
              v-model="model.ref_number"
              type="text"
              readonly
              class="form-control"
            />
            <div class="validate ts-text-xs" v-if="errors.has('stock_in_id')">
              {{ errors.first('stock_in_id') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{ $t('paymentToVendor.vendor') }}</label>
          <div class="ts-w-64">
            <input
              type="text"
              class="form-control"
              readonly
              :value="vendorName"
            />
            <div class="validate ts-text-xs" v-if="errors.has('vendor_id')">
              {{ errors.first('vendor_id') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{ $t('paymentToVendor.billAmount') }}</label>
          <div class="ts-w-64">
            <input
              v-model="model.bill_amount"
              type="text"
              class="form-control"
              readonly
            />
            <div class="validate ts-text-xs" v-if="errors.has('bill_amount')">
              {{ errors.first('bill_amount') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.discountRate')
          }}</label>
          <div class="ts-w-64">
            <input
              v-model.number="model.discount_rate"
              @input="calculate"
              type="number"
              class="form-control"
            />
            <div class="validate ts-text-xs" v-if="errors.has('discount_rate')">
              {{ errors.first('discount_rate') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.discountType')
          }}</label>
          <div class="ts-w-64">
            <select
              v-model="model.discount_type"
              class="form-control"
              @change="calculate"
            >
              <option value="%">%</option>
              <option value="$">$</option>
            </select>
            <div class="validate ts-text-xs" v-if="errors.has('discount_type')">
              {{ errors.first('discount_type') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{
            $t('paymentToVendor.discountAmount')
          }}</label>
          <div class="ts-w-64">
            <input
              v-model.number="model.discount_amount"
              readonly
              type="number"
              step="any"
              class="form-control"
            />
            <div
              class="validate ts-text-xs"
              v-if="errors.has('discount_amount')"
            >
              {{ errors.first('discount_amount') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{ $t('paymentToVendor.amountPaid') }}</label>
          <div class="ts-w-64">
            <input
              v-model.number="model.amount_paid"
              @input="calculate"
              type="number"
              step="any"
              class="form-control"
            />
            <div class="validate ts-text-xs" v-if="errors.has('amount_paid')">
              {{ errors.first('amount_paid') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label class="required">{{ $t('paymentToVendor.amountDue') }}</label>
          <div class="ts-w-64">
            <input
              v-model.number="model.amount_due"
              type="number"
              class="form-control"
              readonly
            />
            <div class="validate ts-text-xs" v-if="errors.has('amount_due')">
              {{ errors.first('amount_due') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label :class="model.amount_due ? 'required' : null">{{
            $t('paymentToVendor.nextPaymentDate')
          }}</label>
          <div class="ts-w-64">
            <a-date-picker
              v-model="model.next_payment_date"
              format="DD-MM-YYYY"
              valueFormat="DD-MM-YYYY"
              class="ts-w-full"
            ></a-date-picker>
            <div
              class="validate ts-text-xs"
              v-if="errors.has('next_payment_date')"
            >
              {{ errors.first('next_payment_date') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center ts-mb-3">
          <label class="required">{{
            $t('paymentToVendor.paymentMethod')
          }}</label>
          <div class="ts-w-64">
            <a-select
              class="ts-w-full"
              v-model="model.payment_method_id"
              :options="paymentMethod"
              @change="fetchBank"
              showSearch
              :filterOption="
                (input, option) =>
                  option.componentOptions.children[0].text
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              "
            ></a-select>
            <div
              class="validate ts-text-xs"
              v-if="errors.has('payment_method_id')"
            >
              {{ errors.first('payment_method_id') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center"
          v-if="isShow([3,4])"
        >
          <label class="required">{{ $t('paymentToVendor.bankName') }}</label>
          <div class="ts-w-64">
            <a-select
              v-model="model.bank_name"
              :options="bankNameOptions"
              class="ts-w-full"
              @change="bankNameChange"
            ></a-select>
            <!-- <input
              v-model="model.bank_name"
              type="text"
              class="form-control"
            /> -->
            <div class="validate ts-text-xs" v-if="errors.has('bank_name')">
              {{ errors.first('bank_name') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center"
          v-if="isShow([4])"
        >
          <label class="required">{{ $t('paymentToVendor.cardType') }}</label>
          <div class="ts-w-64">
            <select
              v-model="model.card_type"
              type="text"
              class="form-control"
            >
              <option value="visa">Visa</option>
              <option value="master">Master card</option>
            </select>
            <div class="validate ts-text-xs" v-if="errors.has('card_type')">
              {{ errors.first('card_type') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center"
          v-if="isShow([2,3,4])"
        >
          <label class="required">{{ $t('paymentToVendor.number') }}</label>
          <div class="ts-w-64">
            <a-select
              v-if="isShow([3])"
              v-model="model.account_card_no"
              :options="accountOptions"
              class="ts-w-full"
              @change="accountChange"
            ></a-select>
            <input
              v-else
              v-model="model.account_card_no"
              type="text"
              class="form-control"
            />
            <div class="validate ts-text-xs" v-if="errors.has('account_card_no')">
              {{ errors.first('account_card_no') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center"
          v-if="isShow([3])"
        >
          <label class="required">{{ $t('paymentToVendor.accountName') }}</label>
          <div class="ts-w-64">
            <input
              v-model="model.account_name"
              type="text"
              class="form-control"
            />
            <div class="validate ts-text-xs" v-if="errors.has('account_name')">
              {{ errors.first('account_name') }}
            </div>
          </div>
        </div>
        <div class="ts-flex ts-justify-between ts-items-center">
          <label>{{ $t('paymentToVendor.remark') }}</label>
          <div class="ts-w-64">
            <textarea rows="3" class="form-control" v-model.trim="model.remarks" ></textarea>
            <div class="validate ts-text-xs" v-if="errors.has('remarks')">
              {{ errors.first('remarks') }}
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12 ts-flex ts-justify-end ts-space-x-3">
            <ts-button @click="$emit('cancel')">{{ $t('cancel') }}</ts-button>
            <ts-button
              color="primary"
              outline
              v-if="!isEdit"
              :waiting="waiting_new"
              @click.prevent="onSaveAddNew"
              >{{ $t('save_add_new') }}</ts-button
            >
            <ts-button
              v-if="!isEdit"
              color="primary"
              :waiting="waiting"
              @click.prevent="onSave"
              >{{ $t('save') }}</ts-button
            >
            <ts-button
              v-if="isEdit"
              color="primary"
              :waiting="waiting"
              @click.prevent="onUpdate"
              >{{ $t('update') }}</ts-button
            >
          </div>
        </div>
      </div>
    </ts-loading-banner>
  </div>
</template>

<script>
import moment from 'moment'
import { Errors } from 'form-backend-validation'
import { mapActions, mapState } from 'vuex'
export default {
  name: 'formCreatePaymentToVendor',
  data() {
    return {
      loading: false,
      waiting: false,
      waiting_new: false,
      errors: new Errors(),
      bankProfiles: [],
      paymentMethod: [
        {
          value: 1,
          label: 'Cash',
        },
        {
          value: 2,
          label: 'Cheque',
        },
        {
          value: 3,
          label: 'Bank Transfer',
        },
        {
          value: 4,
          label: 'Credit Card',
        },
      ],
      model: {
        pay_tran_id: null,
        payment_date: moment().format('DD-MM-YYYY'),
        bill_amount: null,
        discount_rate: 0,
        discount_type: '%',
        discount_amount: 0,
        amount_paid: null,
        amount_due: 0,
        next_payment_date: null,
        ref_number: null,
        stock_in_id: null,
        vendor_id: null,
        payment_method_id: 1,
        account_card_no: null,
        bank_name: null,
        account_name: null,
        card_type: null,
        remarks: null,
      },
    }
  },
  async created() {
    this.loading = true
    await this.getStockIn()
    this.setEditData()
    this.loading = false
  },
  computed: {
    ...mapState('supplier/payment', ['stockin', 'edit_data']),
    bankNameOptions(){
      return this.bankProfiles.map(el => ({
        value: el.bank_name,
        label: el.bank_name,
      }))
    },
    accountOptions(){
      let acc =  this.bankProfiles.filter(b => b.bank_name == this.model.bank_name)[0]
      if(acc){
        return acc.accounts.map(el => ({
          value: el.account_number,
          label: el.account_number,
          name: el.account_name
        }))
      }
      return []
    },
    isEdit() {
      return !_.isEmpty(this.edit_data)
    },
    vendorName() {
      if (this.model.stock_in_id) {

        if(this.isEdit){
          return this.edit_data.supplier.vendor_name
        }

        return this.stockin
          .filter((s) => s.stock_in_id == this.model.stock_in_id)
          .reduce((i, v) => (i = v.supplier.vendor_name), null)
      }
      return null
    },
    isShow: vm => arrayValue => {
      return _.includes(arrayValue, vm.model.payment_method_id)
    },
    stockIn() {
      return this.stockin.map((el) => ({
        value: el.stock_in_id,
        label: el.stock_in_number,
      }))
    },
  },
  methods: {
    ...mapActions('supplier/payment', ['getStockIn', 'store', 'update', 'getBankProfile']),
    onStockChange(value, options) {
      let result = this.stockin.filter((s) => s.stock_in_id == value)
      if (result.length > 0) {
        this.model.bill_amount = result[0].invoice_amount
        this.model.vendor_id = result[0].vendor_id
        this.model.ref_number = result[0].invoice_no
      }
      this.calculate()
    },
    calculate() {
      if (this.model.discount_type == '$') {
        this.model.discount_amount = this.model.discount_rate
      } else {
        this.model.discount_amount = _.round(
          this.model.bill_amount * (this.model.discount_rate / 100),
          2
        )
      }

      // this.model.amount_paid = _.round(this.model.bill_amount - this.model.discount_amount, 2)

      this.model.amount_due = _.round(this.model.bill_amount - (this.model.amount_paid + this.model.discount_amount), 2)

      if (this.model.amount_due > 0) {
        this.model.next_payment_date = moment()
          .add(7, 'days')
          .format('DD-MM-YYYY')
      } else {
        this.model.next_payment_date = null
      }
    },
    onUpdate() {
      this.waiting = true
      this.update({
        id: this.model.pay_tran_id,
        data: this.model,
      })
        .then((response) => {
          this.$toasted.success(response.message)
          this.$emit('cancel')
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSave() {
      this.waiting = true
      this.store(this.model)
        .then((response) => {
          this.$toasted.success(response.message)
          this.$emit('cancel')
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting = false
        })
    },
    onSaveAddNew() {
      this.waiting_new = true
      this.store(this.model)
        .then((response) => {
          this.$toasted.success(response.message)
          this.clearInput()
        })
        .catch((error) => {
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => {
          this.waiting_new = false
        })
    },
    fetchBank(){
      if(this.model.payment_method_id == 3){
        this.loading = true
        this.getBankProfile()
          .then(response => {
            this.bankProfiles =  response.data
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
          .finally(() => {
            this.loading = false
          })
      }else {
        this.model.bank_name = null
        this.model.account_card_no = null
        this.model.account_name = null
      }
    },
    bankNameChange(){
      this.model.account_card_no = null
      this.model.account_name = null
    },
    accountChange(value, opt){
      this.model.account_name = opt.data.props.name
    },
    async clearInput() {
      this.loading = true
      await this.getStockIn()
      this.loading = false
      this.model.payment_date = moment().format('DD-MM-YYYY')
      this.model.bill_amount = null
      this.model.discount_rate = 0
      this.model.discount_type = '%'
      this.model.discount_amount = 0
      this.model.amount_paid = null
      this.model.amount_due = 0
      this.model.next_payment_date = null
      this.model.ref_number = null
      this.model.stock_in_id = null
      this.model.vendor_id = null
      this.model.payment_method_id = 1
      this.model.account_card_no = null
      this.model.bank_name = null
      this.model.account_name = null
      this.model.card_type = null
      this.model.remarks = null
    },
    setEditData() {
      if (!_.isEmpty(this.edit_data)) {
        this.model.pay_tran_id = this.edit_data.pay_tran_id
        this.model.payment_date = this.edit_data.payment_date
        this.model.bill_amount = this.edit_data.bill_amount
        this.model.discount_rate = this.edit_data.discount_rate
        this.model.discount_type = this.edit_data.discount_type
        this.model.discount_amount = this.edit_data.discount_amount
        this.model.amount_paid = this.edit_data.amount_paid
        this.model.amount_due = this.edit_data.amount_due
        this.model.next_payment_date = this.edit_data.next_payment_date
        this.model.ref_number = this.edit_data.ref_number
        this.model.stock_in_id = this.edit_data.stock_in_id
        this.model.vendor_id = this.edit_data.vendor_id
        this.model.payment_method_id = this.edit_data.payment_method_id
        this.model.account_card_no = this.edit_data.account_card_no
        this.model.bank_name = this.edit_data.bank_name
        this.model.account_name = this.edit_data.account_name
        this.model.card_type = this.edit_data.card_type
        this.model.remarks = this.edit_data.remarks

        this.$store.commit('supplier/payment/PUSH_STOCK_IN_LIST', {
          invoice_amount: this.edit_data.bill_amount,
          invoice_no: this.edit_data.ref_number,
          stock_in_id: this.edit_data.stock_in_id,
          stock_in_number: this.edit_data.stock_in.stock_in_number,
          supplier: {
            vendor_id: this.edit_data.vendor_id, 
            vendor_name: this.edit_data.supplier.vendor_name
          },
          vendor_id: this.edit_data.vendor_id,
        })
        this.fetchBank()
      }
    },
  },
}
</script>